import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, connect } from 'react-redux';
import { compose } from 'redux';
import { createStructuredSelector } from 'reselect';
import { getIn } from 'formik';
import { useTranslation } from '../../../../src/i18n';
import { TP, PERFORMANCE_TYPES_ID, VIDEO_ATTRIBUTE_SLUGS } from '../../../../constants/consts';
import { getTimezonesOptions } from '../../../../utils/common';
import { updateOne } from '../../../App/actions';
import { MEDIA } from '../../../../constants/endpointConstants';
import { createDigitalEventAction, deleteDigitalEventAction, updateLivestreamAction } from '../../actions';
import {
  selectLivestreams,
  selectLivesEvents,
  selectProductionUpdating,
  selectLoadingFieldName,
  selectProduction,
} from '../../selectors';
import { selectUserConstants } from '../../../../store/selectors';
import DigitalEventModal from '../DigitalEventModal';
import { livestreamCardFields } from '../DigitalEventModal/consts';
import EditMediaLayout from '../../../../components/EditComponents/EditMediaLayout';
import FbCircularProgress from '../../../../components/FbCircularProgress';
import LivestreamEditCard from '../LivestreamEditCard';
import { getMediaBodyForModifying } from '../../utils';
import { DIGITAL_EVENT_TYPES } from '../../constants';
import ConfirmDeleteModal from '../../../../components/EditComponents/ConfirmDeleteModal';

const LivestreamEvents = ({ production, livestreams, loadingLivestreamId, liveEvents, className }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const timezonesOptions = React.useCallback(() => getTimezonesOptions(), []);

  const openLivestreamModal = livestream => {
    setLivestreamToEdit(livestream);
  };

  const closeLivestreamModal = () => {
    setLivestreamToEdit(null);
  };

  const [livestreamToEdit, setLivestreamToEdit] = React.useState(null);
  const [livestreamToDelete, setLivestreamToDelete] = React.useState(null);

  const saveLivestreamData = livestreamFormValues => {
    const isExistLivestream = !!livestreamFormValues?.id;
    const updatedMedia = getMediaBodyForModifying({
      specificAttributes: [
        livestreamCardFields.startTime,
        livestreamCardFields.dateFrom,
        livestreamCardFields.timezone,
      ],
      recordToEdit: livestreamToEdit,
      recordFormValues: livestreamFormValues,
      productionId: production?.id,
      recordTypeSlug: VIDEO_ATTRIBUTE_SLUGS.LIVESTREAM,
      formFields: livestreamCardFields,
    });
    const image = getIn(livestreamFormValues, livestreamCardFields.thumbnail.name);

    if (isExistLivestream) {
      closeLivestreamModal();
      dispatch(
        updateOne({
          endpoint: MEDIA,
          sagaRoutine: updateLivestreamAction,
          fieldName: livestreamFormValues?.id,
          id: livestreamFormValues?.id,
          params: updatedMedia,
          ...(image ? { files: { [image?.filename]: image?.file } } : {}),
        }),
      );
    } else {
      dispatch(
        createDigitalEventAction({
          params: updatedMedia,
          ...(image ? { files: { [image?.filename]: image?.file } } : {}),
          fieldName: 'new',
          typeId: PERFORMANCE_TYPES_ID.LIVESTREAM,
          typeName: t(`${TP}.FN_LIVESTREAM`),
          callback: closeLivestreamModal,
          production,
          listLength: livestreams?.data?.length,
        }),
      );
    }
  };

  const removeLivestream = () => {
    setLivestreamToDelete(null);
    dispatch(
      deleteDigitalEventAction({
        id: livestreamToDelete?.id,
        fieldName: livestreamToDelete?.id,
        typeId: PERFORMANCE_TYPES_ID.LIVESTREAM,
        typeName: t(`${TP}.FN_LIVESTREAM`),
        production,
        listLength: livestreams?.data?.length,
      }),
    );
  };

  return (
    <>
      <EditMediaLayout
        title={t(`${TP}.FN_LIVESTREAM`)}
        subtitle={t(`${TP}.FN_UPDATE_PRODUCTION_LIVESTREAM_SUBTITLE`)}
        isEmpty={!livestreams?.data?.length}
        buttonTitle={`${TP}.FN_ADD_LIVESTREAM`}
        addButtonClick={() => openLivestreamModal({})}
        emptyMessage={`${TP}.FN_NO_LIVESTREAM`}
        className={className}
      >
        {livestreams?.loading ? (
          <FbCircularProgress />
        ) : (
          livestreams?.data?.map(lsRecord => (
            <LivestreamEditCard
              key={lsRecord?.id}
              livestream={lsRecord}
              onDelete={() => setLivestreamToDelete(lsRecord)}
              onEdit={() => openLivestreamModal(lsRecord)}
              loading={loadingLivestreamId === lsRecord?.id}
            />
          ))
        )}
      </EditMediaLayout>
      {livestreamToEdit && (
        <DigitalEventModal
          onClose={closeLivestreamModal}
          onSave={saveLivestreamData}
          recordInfo={livestreamToEdit}
          timezonesOptions={timezonesOptions()}
          production={production}
          isLoading={loadingLivestreamId === 'new'}
          liveDates={liveEvents}
          type={DIGITAL_EVENT_TYPES.LIVESTREAM}
        />
      )}
      {livestreamToDelete && (
        <ConfirmDeleteModal
          closeModal={() => setLivestreamToDelete(null)}
          title={t(`${TP}.FN_DELETE_LIVESTREAM_CONFIRMATION`)}
          onDelete={removeLivestream}
        />
      )}
    </>
  );
};

LivestreamEvents.propTypes = {
  production: PropTypes.object,
  loadingLivestreamId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  livestreams: PropTypes.object,
  liveEvents: PropTypes.array,
  className: PropTypes.string,
};

LivestreamEvents.defaultProps = {
  className: '',
};

const mapStateToProps = createStructuredSelector({
  userConstants: selectUserConstants(),
  livestreams: selectLivestreams(),
  loadingLivestreamId: selectLoadingFieldName(),
  liveEvents: selectLivesEvents(),
  productionUpdating: selectProductionUpdating(),
  production: selectProduction(),
});

const withConnect = connect(mapStateToProps, null);

export default compose(memo, withConnect)(LivestreamEvents);
